/**
 * Utility for making API calls to the Admin Gateway
 * Formatted according to the API documentation and test script
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.athenex.xyz";
const ADMIN_API_PATH = "/admin";

// Helper function to get authentication headers
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('admin-token');
  const apiKey = localStorage.getItem('admin-api-key');
  const adminEmail = localStorage.getItem('admin-email');
  
  const headers: Record<string, string> = {};
  
  // If we have an API key, use it as the primary auth method
  if (apiKey) {
    headers['X-API-Key'] = apiKey; // Updated to match the example code
  } 
  // Fallback to JWT token if no API key is available
  else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (adminEmail) {
    headers['X-Admin-Email'] = adminEmail;
  }
  
  return headers;
}

// Generic fetch wrapper for admin endpoints
export async function adminFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Format endpoint to ensure it has leading slash
  const formattedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // For GET requests, we leave the trailing slash - tests show this works
  // For other methods, remove trailing slash to avoid 307 redirects
  let finalEndpoint = formattedEndpoint;
  const method = options.method?.toUpperCase() || 'GET';
  if (method !== 'GET' && finalEndpoint.endsWith('/')) {
    finalEndpoint = finalEndpoint.slice(0, -1);
  }
      
  const url = `${API_BASE_URL}${ADMIN_API_PATH}${finalEndpoint}`;
  
  // Create headers object from options or make a new one
  const headers = new Headers(options.headers || {});
  
  // DO NOT include content-type for GET requests - this is critical to avoid validation errors
  if (method === 'GET' && headers.has('Content-Type')) {
    headers.delete('Content-Type');
  }
  
  // Add authentication headers
  const authHeaders = getAuthHeaders();
  Object.entries(authHeaders).forEach(([key, value]) => {
    headers.set(key, value);
  });
  
  // Add Content-Type header for POST/PUT only
  if (method !== 'GET' && !headers.has('Content-Type') && !endpoint.includes('auth/token')) {
    headers.set('Content-Type', 'application/json');
  }
  
  console.log(`Admin fetch ${method} ${url}`);
  console.log('Headers:', Object.fromEntries(headers.entries()));

  // For debugging 422 errors
  if (method === 'GET') {
    console.log('GET request - ensuring no body is sent');
    delete options.body;
  }

  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // Improved error logging
  if (!response.ok) {
    console.error(`Error ${response.status} from ${url}: ${response.statusText}`);
    let errorText: string;
    try {
      errorText = await response.text();
      console.error("Error response:", errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        console.error("Parsed error response:", errorData);
        if (errorData.detail) {
          const detail = typeof errorData.detail === 'string' 
            ? errorData.detail 
            : JSON.stringify(errorData.detail);
          throw new Error(`API error: ${detail}`);
        }
      } catch (e) {
        // Not JSON or no detail field
      }
      
    } catch (e) {
      errorText = "Could not read error response";
    }
    
    throw new Error(`API error ${response.status}: ${response.statusText}`);
  }
  
  // Special case for 204 No Content responses
  if (response.status === 204) {
    return {} as T;
  }
  
  // Handle different response types
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return await response.json();
  } else if (contentType?.includes('application/octet-stream')) {
    return await response.blob() as unknown as T;
  } else {
    return await response.text() as unknown as T;
  }
}

// Special function for updating user credits - using query parameters as required by the API
export async function updateUserCredits(userId: string, credits: number): Promise<any> {
  // IMPORTANT: Remove trailing slash to avoid 307 redirects
  const url = `${API_BASE_URL}${ADMIN_API_PATH}/users/${userId}/credits?credits=${credits}`;
  
  // Get stored authentication credentials
  const token = localStorage.getItem('admin-token');
  const apiKey = localStorage.getItem('admin-api-key');
  const adminEmail = localStorage.getItem('admin-email');
  
  const headers: Record<string, string> = {};
  
  // Include all available authentication headers
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (apiKey) {
    headers['API-Key'] = apiKey; // Correct capitalization
  }
  
  if (adminEmail) {
    headers['X-Admin-Email'] = adminEmail;
  }
  
  console.log(`Updating credits: ${url}`);
  console.log('Headers:', headers);
  
  const response = await fetch(url, {
    method: 'PUT',
    headers
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error("Error updating credits:", errorText);
    throw new Error(`Failed to update credits: ${response.statusText}`);
  }
  
  // Check if response is no content (204)
  if (response.status === 204) {
    return {}; // Return empty object for no content
  }
  
  return response.json();
}

// Add the special methods we created in adminApi.ts
export async function createUserApiKey(userId: string): Promise<any> {
  return adminFetch(`/users/${userId}/api-key`, {
    method: 'POST'
  });
}

export async function getDatabaseStats(): Promise<any> {
  return adminFetch('/database/stats');
}

export async function getDatabaseCollections(): Promise<any> {
  return adminFetch('/database/collections');
}

export async function exportDatabaseCollection(collectionName: string): Promise<any> {
  return adminFetch(`/database/export/${collectionName}`);
}

// Special debug function for troubleshooting 422 errors
export async function debugRequest(endpoint: string): Promise<any> {
  const url = `${API_BASE_URL}${ADMIN_API_PATH}${endpoint}`;
  
  const token = localStorage.getItem('admin-token');
  const apiKey = localStorage.getItem('admin-api-key');
  const adminEmail = localStorage.getItem('admin-email');
  
  // Try multiple header combinations
  const headerSets = [
    // Set 1: Token only
    token ? { 'Authorization': `Bearer ${token}` } : null,
    
    // Set 2: API key only (capitalized)
    apiKey ? { 'API-Key': apiKey } : null,
    
    // Set 3: API key only (lowercase)
    apiKey ? { 'api-key': apiKey } : null,
    
    // Set 4: Token + Email
    token && adminEmail ? { 
      'Authorization': `Bearer ${token}`,
      'X-Admin-Email': adminEmail 
    } : null,
    
    // Set 5: API key + Email
    apiKey && adminEmail ? { 
      'API-Key': apiKey,
      'X-Admin-Email': adminEmail 
    } : null,
    
    // Set 6: All headers
    token && apiKey && adminEmail ? {
      'Authorization': `Bearer ${token}`,
      'API-Key': apiKey,
      'X-Admin-Email': adminEmail
    } : null,
  ].filter(Boolean); // Remove null entries
  
  console.log(`Debug request testing ${headerSets.length} header combinations for ${url}`);
  
  for (let i = 0; i < headerSets.length; i++) {
    const headers = headerSets[i]!;
    console.log(`Test #${i+1} with headers:`, headers);
    
    try {
      const response = await fetch(url, { headers });
      console.log(`Test #${i+1} response: ${response.status} ${response.statusText}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`Test #${i+1} SUCCESS with data:`, data);
        return { success: true, headers, data };
      } else {
        const text = await response.text();
        console.log(`Test #${i+1} FAILED with error:`, text);
      }
    } catch (e) {
      console.error(`Test #${i+1} threw exception:`, e);
    }
  }
  
  return { success: false, message: "All header combinations failed" };
}

export default adminFetch;
